import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Container, Grid, Paper, Fade } from '@mui/material';
import styled, { keyframes } from 'styled-components';
import { 
    ArrowForward as ArrowForwardIcon,
    AdminPanelSettings as AdminIcon,
    School as StudentIcon,
    Person as TeacherIcon,
    FamilyRestroom as ParentIcon
} from '@mui/icons-material';

const Homepage = () => {
    const navigate = useNavigate();
    const [showChooser, setShowChooser] = useState(false);

    const handleNavigate = (path) => {
        navigate(path);
    };

    return (
        <StyledContainer>
            {/* Modern Abstract Background Elements */}
            <Blob1 />
            <Blob2 />
            <GridPattern />

            <ContentWrapper>
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                    <Grid container spacing={6} alignItems="center" justifyContent="center">
                        
                        {/* Hero Content */}
                        {!showChooser && (
                            <Grid 
                                item 
                                xs={12} 
                                sx={{ 
                                    textAlign: 'center',
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center' 
                                }}
                            >
                                <Fade in={!showChooser} timeout={400}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <StyledTitle 
                                            variant="h1" 
                                            sx={{ 
                                                textAlign: 'center', 
                                                whiteSpace: { xs: 'normal', md: 'nowrap' }
                                            }}
                                        >
                                             Student Management System 
                                        </StyledTitle>

                                        <StyledSubtitle sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            Smart. Simple. Seamless.
                                        </StyledSubtitle>

                                        <Box mt={2}>
                                            <PrimaryButton 
                                                variant="contained" 
                                                endIcon={<ArrowForwardIcon />}
                                                size="large"
                                                onClick={() => setShowChooser(true)}
                                            >
                                                Go to Dashboard
                                            </PrimaryButton>
                                        </Box>
                                    </Box>
                                </Fade>
                            </Grid>
                        )}

                        {/* Account Chooser (Animated) */}
                        {showChooser && (
                            <Grid item xs={12} md={8} lg={6}>
                                <Fade in={showChooser} timeout={600}>
                                    <ChooserContainer elevation={0}>
                                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 1, letterSpacing: '-0.02em' }}>
                                            Welcome Back
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: '#64748b', mb: 4 }}>
                                            Please select your account type to access the platform.
                                        </Typography>

                                        <Grid container spacing={3} justifyContent="center">
                                            <Grid item xs={12} sm={4}>
                                                <AccountCard onClick={() => handleNavigate('/Studentlogin')}>
                                                    <IconWrapper className="student">
                                                        <StudentIcon sx={{ fontSize: 36 }} />
                                                    </IconWrapper>
                                                    <Typography variant="subtitle1" fontWeight="700">Student</Typography>
                                                </AccountCard>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <AccountCard onClick={() => handleNavigate('/Teacherlogin')}>
                                                    <IconWrapper className="teacher">
                                                        <TeacherIcon sx={{ fontSize: 36 }} />
                                                    </IconWrapper>
                                                    <Typography variant="subtitle1" fontWeight="700">Teacher</Typography>
                                                </AccountCard>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                {/* <AccountCard onClick={() => handleNavigate('/Parentlogin')}> */}
                                                 <AccountCard onClick={() => handleNavigate('#')}>
                                                    <IconWrapper className="parent">
                                                        <ParentIcon sx={{ fontSize: 36 }} />
                                                    </IconWrapper>
                                                    <Typography variant="subtitle1" fontWeight="700">Parent</Typography>
                                                </AccountCard>
                                            </Grid>
                                        </Grid>
                                    </ChooserContainer>
                                </Fade>
                            </Grid>
                        )}
                    </Grid>
                </Container>
            </ContentWrapper>

            <Footer>
                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                    © {new Date().getFullYear()} Arphatech Solution. All rights reserved.
                </Typography>
            </Footer>
        </StyledContainer>
    );
};

export default Homepage;

/* ===== ANIMATIONS & STYLED COMPONENTS ===== */

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
`;

const float1 = keyframes`
    0% { transform: translate(0, 0) rotate(0deg); }
    33% { transform: translate(30px, -50px) rotate(10deg); }
    66% { transform: translate(-20px, 20px) rotate(-5deg); }
    100% { transform: translate(0, 0) rotate(0deg); }
`;

const float2 = keyframes`
    0% { transform: translate(0, 0) rotate(0deg); }
    33% { transform: translate(-30px, 40px) rotate(-10deg); }
    66% { transform: translate(20px, -20px) rotate(5deg); }
    100% { transform: translate(0, 0) rotate(0deg); }
`;

const StyledContainer = styled.div`
    height: 100vh;
    width: 100%;
    background-color: #fafafa;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const GridPattern = styled.div`
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background-image: 
        linear-gradient(to right, rgba(99, 102, 241, 0.05) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(99, 102, 241, 0.05) 1px, transparent 1px);
    background-size: 40px 40px;
    z-index: 1;
    pointer-events: none;
    mask-image: radial-gradient(circle at center, black 40%, transparent 80%);
    -webkit-mask-image: radial-gradient(circle at center, black 40%, transparent 80%);
`;

const Blob1 = styled.div`
    position: absolute;
    top: -10%; left: -10%;
    width: 600px; height: 600px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, rgba(99, 102, 241, 0) 70%);
    border-radius: 50%;
    animation: ${float1} 15s ease-in-out infinite;
    z-index: 0;
    pointer-events: none;
`;

const Blob2 = styled.div`
    position: absolute;
    bottom: -20%; right: -10%;
    width: 800px; height: 800px;
    background: radial-gradient(circle, rgba(168, 85, 247, 0.15) 0%, rgba(168, 85, 247, 0) 70%);
    border-radius: 50%;
    animation: ${float2} 20s ease-in-out infinite;
    z-index: 0;
    pointer-events: none;
`;

const ContentWrapper = styled(Box)`
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 10;
    animation: ${fadeIn} 0.6s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
`;

const StyledTitle = styled(Typography)`
    && {
        font-size: 5rem;
        font-weight: 900;
        letter-spacing: -0.04em;
        margin-bottom: 8px;
        background: linear-gradient(135deg, #0f172a 0%, #475569 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;

        @media (max-width: 900px) {
            font-size: 4rem;
        }

        @media (max-width: 600px) {
            font-size: 3.5rem;
        }
    }
`;

const StyledSubtitle = styled(Typography)`
    && {
        font-size: 1.5rem;
        color: #64748b;
        font-weight: 500;
        margin-bottom: 40px;
        letter-spacing: 0.02em;

        @media (max-width: 600px) {
            font-size: 1.25rem;
            margin-bottom: 32px;
        }
    }
`;

const PrimaryButton = styled(Button)`
    && {
        background: #0f172a;
        color: white;
        padding: 16px 48px;
        border-radius: 100px;
        font-size: 1.15rem;
        font-weight: 700;
        text-transform: none;
        box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.3);
        transition: all 0.3s ease;

        &:hover {
            transform: translateY(-3px);
            box-shadow: 0 20px 35px -5px rgba(15, 23, 42, 0.4);
            background: #1e293b;
        }
    }
`;

const ChooserContainer = styled(Paper)`
    padding: 40px;
    border-radius: 32px !important;
    background: rgba(255, 255, 255, 0.8) !important;
    backdrop-filter: blur(20px) !important;
    border: 1px solid rgba(255, 255, 255, 0.9) !important;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.1) !important;
    text-align: center;
`;

const AccountCard = styled(Box)`
    padding: 24px 16px;
    border-radius: 20px;
    background: white;
    border: 1px solid #f1f5f9;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);

    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        border-color: #e2e8f0;

        .admin { background: #fee2e2; color: #ef4444; }
        .student { background: #e0e7ff; color: #4f46e5; }
        .teacher { background: #dcfce7; color: #22c55e; }
        .parent { background: #fef3c7; color: #f59e0b; }
    }
`;

const IconWrapper = styled(Box)`
    width: 64px;
    height: 64px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8fafc;
    color: #64748b;
    transition: all 0.3s ease;
`;

const Footer = styled(Box)`
    position: relative;
    z-index: 2;
    padding: 24px;
    text-align: center;
    background: transparent;
`;
